/**
 * Desktop "new feedback" notifications.
 *
 * The desktop shell can't use Web Push (no service-worker push in the Tauri
 * webview), so while the app is running — including hidden in the tray — we
 * poll `GET /me/feedback-activity` and raise native OS notifications through
 * the Tauri notification plugin. Inert on web (`isDesktop === false`); the
 * browser path uses Web Push instead (src/lib/push.ts).
 */
import * as api from './api';
import { isDesktop } from './desktop';

const POLL_INTERVAL_MS = 60_000;
const LAST_SEEN_KEY = 'pollenate-feedback-last-seen';
/** Don't spam a wall of toasts when a poll returns a burst of feedback. */
const MAX_TOASTS_PER_POLL = 3;

let timer: ReturnType<typeof setInterval> | null = null;
let polling = false;

function getLastSeen(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

function setLastSeen(iso: string): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, iso);
  } catch {
    /* storage unavailable — we'll just re-notify next launch */
  }
}

async function ensurePermission(): Promise<boolean> {
  const { isPermissionGranted, requestPermission } = await import(
    '@tauri-apps/plugin-notification'
  );
  if (await isPermissionGranted()) return true;
  return (await requestPermission()) === 'granted';
}

async function poll(): Promise<void> {
  if (polling) return; // skip if the previous poll is still in flight
  polling = true;
  try {
    const since = getLastSeen();
    const { items, latest } = await api.getFeedbackActivity(since);

    // First run: just establish the high-water mark, don't replay history.
    if (!since) {
      setLastSeen(latest);
      return;
    }
    if (items.length === 0) return;
    setLastSeen(latest);

    if (!(await ensurePermission())) return;
    const { sendNotification } = await import('@tauri-apps/plugin-notification');

    const toShow = items.slice(0, MAX_TOASTS_PER_POLL);
    for (const item of toShow) {
      const score = item.score !== null ? ` (${item.score})` : '';
      sendNotification({
        title: `New feedback in ${item.inboxName}`,
        body: item.comment || `New ${item.type} feedback${score}`,
      });
    }
    const overflow = items.length - toShow.length;
    if (overflow > 0) {
      sendNotification({
        title: 'Pollenate',
        body: `…and ${overflow} more new feedback item${overflow === 1 ? '' : 's'}.`,
      });
    }
  } catch {
    // Network hiccup or expired session — the next interval retries, and the
    // app's normal 401 handling deals with auth.
  } finally {
    polling = false;
  }
}

/**
 * Start the background poll. Call when the authenticated app shell mounts;
 * safe to call repeatedly (idempotent) and a no-op on web.
 */
export function startDesktopNotifications(): void {
  if (!isDesktop || timer) return;
  void poll();
  timer = setInterval(() => void poll(), POLL_INTERVAL_MS);
}

/** Stop polling (logout / app shell unmount). */
export function stopDesktopNotifications(): void {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}
